import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCorners, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { ArrowRight, Building2, CalendarDays, CheckSquare, GripVertical, Loader2, MoreHorizontal, UserRound } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";
import { STAGE_DOT } from "@/lib/crm";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/** Opportunity card (compact: name + value + contact/company + next action). */
export const OpportunityCard = ({ opp, stages, currency, onOpen, onMove, onEdit, onClose, moving, dragging = false, overlay = false, canEdit = true, handleProps = {} }) => {
  const { t } = useTranslation();
  const otherStages = (stages || []).filter((s) => s.id !== opp.stage_id);
  return (
    <div
      className={cn("group rounded-lg border border-border bg-card p-3 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-colors duration-150", !overlay && "hover:border-primary/40", dragging && !overlay && "opacity-40", overlay && "rotate-[1deg] shadow-lg ring-2 ring-primary/30")}
      data-testid={`crm-opportunity-card-${opp.id}`}
    >
      <div className="flex items-start gap-2">
        {canEdit && (
          <button type="button" className="og-focus -ml-1 mt-0.5 shrink-0 cursor-grab rounded p-0.5 text-muted-foreground opacity-70 hover:text-foreground active:cursor-grabbing" aria-label={t("crm.pipeline.hint")} data-testid={`crm-opportunity-drag-${opp.id}`} {...handleProps}>
            <GripVertical className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <button type="button" onClick={() => onOpen?.(opp)} className="og-focus min-w-0 flex-1 rounded text-left" data-testid={`crm-opportunity-open-${opp.id}`}>
          <div className="truncate text-sm font-semibold leading-5 text-foreground">{opp.name}</div>
          <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground" data-testid={`crm-opportunity-amount-${opp.id}`}>{opp.amount != null ? formatCurrency(opp.amount, opp.currency || currency) : "—"}{opp.probability != null ? ` · ${opp.probability} %` : ""}</div>
        </button>
        {!overlay && canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" aria-label={t("common.actions")} data-testid={`crm-opportunity-menu-${opp.id}`} onClick={(e) => e.stopPropagation()}>
                {moving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-card">
              <DropdownMenuItem onSelect={() => onOpen?.(opp)} data-testid={`crm-opportunity-action-open-${opp.id}`}>{t("common.open")}</DropdownMenuItem>
              {onEdit && <DropdownMenuItem onSelect={() => onEdit(opp)} data-testid={`crm-opportunity-action-edit-${opp.id}`}>{t("common.edit")}</DropdownMenuItem>}
              {otherStages.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="og-section-label">{t("crm.moveTo")}</DropdownMenuLabel>
                  {otherStages.map((s) => (
                    <DropdownMenuItem key={s.id} onSelect={() => onMove?.(opp, s)} data-testid={`crm-opportunity-move-${opp.id}-${s.key}`}>
                      <span className={cn("mr-2 h-2 w-2 rounded-full", STAGE_DOT[s.color] || "bg-slate-400")} aria-hidden="true" />{s.name}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {onClose && opp.status === "open" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => onClose(opp, "won")} data-testid={`crm-opportunity-close-won-${opp.id}`}>{t("crm.pipeline.closeWon")}</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onClose(opp, "lost")} data-testid={`crm-opportunity-close-lost-${opp.id}`}>{t("crm.pipeline.closeLost")}</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        {(opp.contact_name || opp.company_name) && (
          <div className="flex items-center gap-1.5 truncate">
            {opp.contact_name ? <UserRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            <span className="truncate">{[opp.contact_name, opp.company_name].filter(Boolean).join(" · ")}</span>
          </div>
        )}
        {opp.next_task ? (
          <div className="flex items-center gap-1.5 truncate" data-testid={`crm-opportunity-next-${opp.id}`}>
            <CheckSquare className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
            <span className="truncate">{opp.next_task.title}</span>
            {opp.next_task.due_at && <span className="shrink-0">· {formatDate(opp.next_task.due_at)}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 truncate"><CheckSquare className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span className="truncate">{t("crm.pipeline.noNextAction")}</span></div>
        )}
        {opp.expected_close_date && (
          <div className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span>{t("crm.expectedClose")} : {formatDate(opp.expected_close_date)}</span></div>
        )}
      </div>
    </div>
  );
};

const DraggableCard = ({ opp, canEdit, ...rest }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: opp.id, data: { opp }, disabled: !canEdit });
  const style = { transform: CSS.Translate.toString(transform) };
  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && "z-10")}>
      <OpportunityCard opp={opp} dragging={isDragging} canEdit={canEdit} handleProps={{ ...listeners, ...attributes }} {...rest} />
    </div>
  );
};

const Column = ({ stage, currency, children, isOver, count, total }) => {
  const { t } = useTranslation();
  const { setNodeRef } = useDroppable({ id: stage.id, data: { stage } });
  return (
    <div ref={setNodeRef} className={cn("flex w-[272px] shrink-0 flex-col rounded-xl border bg-muted/40 transition-colors duration-150", isOver ? "border-primary/60 bg-accent/60" : "border-border")} data-testid={`crm-kanban-column-${stage.key}`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", STAGE_DOT[stage.color] || "bg-slate-400")} aria-hidden="true" />
          <span className="truncate font-heading text-sm font-semibold">{stage.name}</span>
          <span className="rounded-md bg-card px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground" data-testid={`crm-kanban-count-${stage.key}`}>{count}</span>
        </div>
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground" data-testid={`crm-kanban-total-${stage.key}`}>{formatCurrency(total || 0, currency)}</span>
      </div>
      <div className="og-scroll flex max-h-[calc(100vh-330px)] min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">
        {children}
        {count === 0 && (
          <div className={cn("flex flex-1 items-center justify-center rounded-lg border border-dashed px-3 py-6 text-center text-xs text-muted-foreground", isOver ? "border-primary/60 text-foreground" : "border-border")} data-testid={`crm-kanban-empty-${stage.key}`}>
            {isOver ? t("crm.pipeline.dropHere", { stage: stage.name }) : t("crm.noOpps")}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Kanban of opportunities. Drag & drop between stages calls `onMove(opp, stage)` (backend business action);
 * the board itself never mutates data. Also exposes a "move to" menu per card (touch/keyboard fallback).
 */
export const KanbanBoard = ({ board, onMove, onOpen, onEdit, onClose, movingId, canEdit = true }) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const stages = board?.stages || [];
  const all = stages.flatMap((s) => s.opportunities || []);
  const active = all.find((o) => o.id === activeId);
  const stageById = (id) => stages.find((s) => s.id === id);

  const onDragEnd = ({ active: a, over }) => {
    setActiveId(null);
    setOverId(null);
    if (!over) return;
    const opp = a.data.current?.opp;
    const target = stageById(over.id) || stageById(over.data.current?.stage?.id);
    if (!opp || !target || target.id === opp.stage_id) return;
    onMove?.(opp, target);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={({ active: a }) => setActiveId(a.id)} onDragOver={({ over }) => setOverId(over?.id || null)} onDragCancel={() => { setActiveId(null); setOverId(null); }} onDragEnd={onDragEnd}>
      {stages.length > 1 && <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground sm:hidden" data-testid="crm-kanban-scroll-hint">{t("crm.pipeline.scrollHint")}<ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /></p>}
      <div className="og-scroll flex w-full min-w-0 max-w-full gap-3 overflow-x-auto overscroll-x-contain scroll-px-3 pb-3 pr-5" data-testid="crm-pipeline-kanban">
        {stages.map((stage) => (
          <Column key={stage.id} stage={stage} currency={board.currency} count={stage.count} total={stage.total_amount} isOver={overId === stage.id && !!activeId}>
            {(stage.opportunities || []).map((opp) => (
              <DraggableCard key={opp.id} opp={opp} stages={stages} currency={board.currency} onOpen={onOpen} onMove={onMove} onEdit={onEdit} onClose={onClose} moving={movingId === opp.id} canEdit={canEdit} />
            ))}
          </Column>
        ))}
      </div>
      <DragOverlay dropAnimation={null}>{active ? <div className="w-[256px]"><OpportunityCard opp={active} stages={stages} currency={board.currency} overlay canEdit /></div> : null}</DragOverlay>
    </DndContext>
  );
};
